import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import baseUrl from "../../url/baseUrl";
import HashLoader from "react-spinners/HashLoader";
import "./News.css";

// remove diacritics for robust searching (vi)
const norm = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);

  const categories = ["All", "Tin tức", "Chăm con"];

  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  // debounce 250ms
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setLoading(true);
    const getNews = async () => {
      try {
        const response = await fetch(baseUrl + "/get-news-rumi");
        if (response.status === 404) {
          throw new Error("Không có tin tức");
        }

        if (response.status !== 200) {
          throw new Error("Failed to fetch news.");
        }
        const data = await response.json();

        setLoading(false);
        setNews(data);
      } catch (err) {
        setLoading(false);
        console.log(err);
      }
    };
    getNews();
  }, []);

  const filtered = useMemo(() => {
    const byCat =
      activeCat === "All" ? news : news.filter((n) => n.category === activeCat);
    if (!debounced) {
      return [...byCat].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    }
    const q = norm(debounced);
    return [...byCat]
      .filter((n) => norm(n.title).includes(q) || norm(n.shortDesc).includes(q))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [activeCat, debounced, news]);

  return (
    <section className="news-wrap-page">
      <div className="news-head">
        <p className="eyebrow">RUMI JOURNAL</p>
        <h1>Tin tức &amp; Sự kiện</h1>
        <p className="intro">
          Các tin tức mới nhất cùng những kinh nghiệm chăm sóc bé đến từ đội ngũ
          chuyên gia của Rumi.
        </p>
      </div>

      {/* Search + Filter */}
      <div className="news-toolbar">
        <div className="search">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <circle cx="11" cy="11" r="7" stroke="#6f7b85" fill="none" />
            <line x1="16.5" y1="16.5" x2="22" y2="22" stroke="#6f7b85" />
          </svg>
          <input
            type="search"
            placeholder="Tìm bài viết…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Tìm kiếm tin tức"
          />
          {query && (
            <button
              className="clear"
              onClick={() => setQuery("")}
              aria-label="Xóa tìm kiếm"
            >
              ×
            </button>
          )}
        </div>

        <div className="news-filter" role="tablist" aria-label="Chọn danh mục">
          {categories.map((cat) => {
            const count =
              cat === "All"
                ? news.length
                : news.filter((n) => n.category === cat).length;
            return (
              <button
                key={cat}
                role="tab"
                aria-selected={activeCat === cat}
                className={`filter-chip ${
                  activeCat === cat ? "is-active" : ""
                }`}
                onClick={() => setActiveCat(cat)}
              >
                {cat} <span className="badge">{count}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* show loading spinner */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center">
          <HashLoader color="#36d7b7" size={80} />
        </div>
      )}

      {/* Grid */}

      {!loading && filtered.length === 0 ? (
        <p className="empty">Không tìm thấy bài viết phù hợp.</p>
      ) : (
        <div className="news-grid">
          {filtered.map((n) => (
            <article key={n._id} className="news-card">
              <figure className="thumb">
                <img src={n.imageAvatar} alt={n.title} />
                <span className={`chip chip-${n.category.toLowerCase()}`}>
                  {n.category}
                </span>
              </figure>
              <div className="body">
                <h3 className="title">{n.title}</h3>
                <p className="meta">
                  {new Date(n.createdAt).toLocaleDateString("vi-VN")}
                </p>
                <p className="excerpt">{n.shortDesc}</p>
                <Link className="readmore" to={`/news/${n._id}`}>
                  Đọc tiếp →
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
